import { uploadBlob } from '../api/blobUpload';
import { api } from '../api/client';
import {
  buildGuestPasswordVerifier,
  createGuestPasswordSalt,
  deriveGuestPasswordKey,
  encodePasswordVerifierBase64,
} from './guestPassword';
import { deriveProofKeys } from './infoShareFragment';
import {
  buildSharedFolderGuestFragment,
  type SharedFolderGuestFragment,
} from './sharedFolderGuestFragment';
import {
  decryptAesGcm,
  decodeBase64Url,
  encodeBase64Url,
  encryptAesGcm,
  hkdfSha256,
  importAesKey,
  packEncryptedBlob,
  randomBytes,
  unpackEncryptedBlob,
} from './primitives';
import { resolvePasswordPolicy } from './passwordPoliciesApi';
import { assertPasswordPolicy } from './passwordPolicy';

export const FOLDER_GUEST_ENVELOPE_AAD = 'shareweb:folder-guest-envelope:v1';
const GUEST_WRAP_HKDF_INFO = 'shareweb:guest-wrap:v1';

export type SharedFolderGuestPermission = 'Read' | 'ReadWrite';

export async function encryptFolderGuestEnvelope(
  folderKey: Uint8Array,
  guestWrapKey: Uint8Array,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(guestWrapKey);
  const encrypted = await encryptAesGcm(
    aesKey,
    folderKey,
    new TextEncoder().encode(FOLDER_GUEST_ENVELOPE_AAD),
  );
  return packEncryptedBlob(encrypted);
}

export async function decryptFolderGuestEnvelope(
  envelopeBlob: Uint8Array,
  guestWrapKey: Uint8Array,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(guestWrapKey);
  return decryptAesGcm(
    aesKey,
    unpackEncryptedBlob(envelopeBlob),
    new TextEncoder().encode(FOLDER_GUEST_ENVELOPE_AAD),
  );
}

export async function deriveGuestWrapKeyFromPassword(
  password: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const unwrapKey = await deriveGuestPasswordKey(password, salt);
  return hkdfSha256(unwrapKey, new Uint8Array(0), GUEST_WRAP_HKDF_INFO, 32);
}

export async function resolveGuestWrapKey(
  fragment: SharedFolderGuestFragment,
  password?: string,
): Promise<Uint8Array | null> {
  if (fragment.gw) {
    return decodeBase64Url(fragment.gw);
  }

  if (!fragment.salt || !password) {
    return null;
  }

  const salt = decodeBase64Url(fragment.salt);
  return deriveGuestWrapKeyFromPassword(password, salt);
}

export async function createSharedFolderGuestInviteMaterials(input: {
  folderId: string;
  folderKey: Uint8Array;
  permission: SharedFolderGuestPermission;
  label?: string;
  password?: string;
  maxGuestUploadFiles?: number | null;
  maxGuestUploadTotalBytes?: number | null;
  rootPath?: string;
  inviteKind?: 'Standard' | 'QuickShare';
}): Promise<{
  guestUrl: string;
  guestInviteId: string;
  guestPath: string;
  fragment: SharedFolderGuestFragment;
}> {
  const proofSeed = randomBytes(32);
  const { publicKey: proofPublicKey } = deriveProofKeys(proofSeed);

  const password = input.password?.trim() ?? '';
  const usePassword = password.length > 0;
  let guestWrapKey: Uint8Array;
  let passwordVerifier: string | undefined;
  let passwordMode = 'None';
  let fragmentSalt: string | undefined;

  if (usePassword) {
    const policy = await resolvePasswordPolicy('folderGuestPassword');
    assertPasswordPolicy(password, policy);
    const salt = createGuestPasswordSalt();
    guestWrapKey = await deriveGuestWrapKeyFromPassword(password, salt);
    passwordVerifier = encodePasswordVerifierBase64(
      await buildGuestPasswordVerifier(password, salt),
    );
    passwordMode = 'UserSet';
    fragmentSalt = encodeBase64Url(salt);
  } else {
    guestWrapKey = randomBytes(32);
  }

  const envelopeBlob = await encryptFolderGuestEnvelope(input.folderKey, guestWrapKey);
  const envelopeBlobId = await uploadBlob(envelopeBlob);

  const rootPath = (input.rootPath ?? '').replace(/^\/+|\/+$/g, '').replace(/\\/g, '/');
  const fragmentPayload: SharedFolderGuestFragment = {
    v: 1,
    fs: encodeBase64Url(proofSeed),
    ...(usePassword ? { salt: fragmentSalt } : { gw: encodeBase64Url(guestWrapKey) }),
    ...(rootPath ? { rp: rootPath } : {}),
  };

  const created = await api.createSharedFolderGuestInvite(input.folderId, {
    permission: input.permission,
    envelopeBlobId,
    fragmentProofPublicKey: proofPublicKey,
    passwordMode,
    passwordVerifier,
    label: input.inviteKind === 'QuickShare' ? undefined : input.label?.trim() || undefined,
    maxGuestUploadFiles: input.permission === 'ReadWrite' ? input.maxGuestUploadFiles : null,
    maxGuestUploadTotalBytes: input.permission === 'ReadWrite' ? input.maxGuestUploadTotalBytes : null,
    rootPath: rootPath || null,
    inviteKind: input.inviteKind,
  });

  const fragment = buildSharedFolderGuestFragment(fragmentPayload);
  const guestUrl = `${window.location.origin}${created.guestPath}#${fragment}`;
  return {
    guestUrl,
    guestInviteId: created.guestInviteId,
    guestPath: created.guestPath,
    fragment: fragmentPayload,
  };
}

export async function createSharedFolderGuestInvite(input: {
  folderId: string;
  folderKey: Uint8Array;
  permission: SharedFolderGuestPermission;
  label?: string;
  password?: string;
  maxGuestUploadFiles?: number | null;
  maxGuestUploadTotalBytes?: number | null;
  rootPath?: string;
}): Promise<{ guestUrl: string; guestInviteId: string }> {
  const created = await createSharedFolderGuestInviteMaterials(input);
  return { guestUrl: created.guestUrl, guestInviteId: created.guestInviteId };
}
